import {
  actions,
  InputButton,
  KeyMap,
  TextInput,
  useCallbackRef,
} from '@infotech-soe/erp-ui-kit';
import React, {
  forwardRef,
  memo,
  MouseEvent,
  Ref,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useIntl } from 'react-intl';

import { messages } from './messages';
import { LookupBoxProps } from './types';

const LookupBox = (
  props: LookupBoxProps,
  ref: Ref<HTMLInputElement>
): React.JSX.Element => {
  const {
    disabled,
    icon = 'ellipsis' as const,
    keymap: keymapProp,
    titleDictionary,
    titleUpdater,
    onRunSingleUpdater,
    onButtonClick,
    ...rest
  } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const { formatMessage } = useIntl();

  const callbackRef = useCallbackRef(inputRef, ref);

  const handleButtonClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      inputRef.current?.focus();
      onButtonClick?.(event);
    },
    [onButtonClick]
  );

  const handleUpdaterClick = useCallback(() => {
    inputRef.current?.focus();
    onRunSingleUpdater?.();
  }, [onRunSingleUpdater]);

  const keymap = useMemo(
    () => ({
      ...keymapProp,
      ...(onButtonClick && { [KeyMap[actions.CONTROL_POPUP]]: onButtonClick }),
    }),
    [onButtonClick, keymapProp]
  );

  return (
    <TextInput
      {...rest}
      disabled={disabled}
      endSlot={
        <>
          {onRunSingleUpdater && (
            <InputButton
              icon="refresh"
              disabled={disabled}
              title={titleUpdater || formatMessage(messages.updater)}
              onClick={handleUpdaterClick}
            />
          )}
          {onButtonClick && (
            <InputButton
              icon={icon}
              disabled={disabled}
              title={titleDictionary || formatMessage(messages.ellipsis)}
              onClick={handleButtonClick}
            />
          )}
        </>
      }
      keymap={keymap}
      ref={callbackRef}
    />
  );
};

export default memo(forwardRef(LookupBox));
